import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import ButtonGroup from "@mui/material/ButtonGroup";
import {OpenInNewRounded, ArrowBackIosNew, ThumbUpAltRounded} from "@mui/icons-material";
import {notification, Empty, Layout, Row, Col, Card, Space, Divider, Typography} from 'antd';
import PortfolioSignalsTable from "./PortfolioSignalsTable";
import Alert from "@mui/material/Alert"
import AlertTitle from "@mui/material/AlertTitle"
import {fetchDetailedPortfolioObjects, saveUserPortfolioSnapshot, fetchUserPortfolioHistory} from '../../../api/portfolioApi';
import { selectFullPortfolios } from "../../../store/slices/portfolios.slice";
import PageHeader from "../../../components/PageHeader";
import {toDateTime} from "../../../utils/datetime";
import ThirdPartyExports from "../ThirdPartyExports";

const { Content } = Layout;


const formatWeightsForSubmission = (dataSource) => {
  return dataSource.map(({ id, weight }) => ({
    id,
    weight: parseFloat(weight),
  }));
};


const PortfolioSignals = () => {
    const portfolioId = useParams()?.portfolioId;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const detailedPortfolio = useSelector(state => selectFullPortfolios(state, [portfolioId]))[0];
    const [initialDataSource, setInitialDataSource] = useState([]);
    const [historyDataSource, setHistoryDataSource] = useState([]);
    const [historyTimestamp, setHistoryTimestamp] = useState(null);
    const [loadingPortfolio, setLoadingPortfolio] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [showExport, setShowExport] = useState(false);
    const [showSider, setShowSider] = useState(true);


    useEffect(() => {
        if (portfolioId) {
            setLoadingPortfolio(true);
            setLoadingHistory(true);

            // Dispatch to fetch the detailed portfolio
            dispatch(fetchDetailedPortfolioObjects([portfolioId]))
                .then(() => {
                    setLoadingPortfolio(false);
                })
                .catch(error => {
                    notification.error({
                        message: 'Error Fetching Portfolio',
                        description: `There was an error fetching the portfolio details: ${error.toString()}`
                    });
                });

            // Dispatch to fetch the user portfolio history
            dispatch(fetchUserPortfolioHistory({portfolioId}))
                .then((history) => {
                    if (history.payload && history.payload.historyData.length > 0) {
                        const historyData = history.payload.historyData[0].snapshot;
                        const timestamp = toDateTime(history.payload.historyData[0].timestamp);
                        setHistoryDataSource(historyData);
                        setHistoryTimestamp(timestamp);
                        setLoadingHistory(false);
                    } else {
                        setLoadingHistory(false); // Set loading false even if no data
                    }
                })
                .catch((error) => {
                    notification.error({
                        message: 'Error Fetching History',
                        description: `There was an error fetching the portfolio history: ${error.toString()}`
                    });
                });
        }
    }, [dispatch, portfolioId]);


    useEffect(() => {
        if (!loadingPortfolio && !loadingHistory && detailedPortfolio && detailedPortfolio.symbols) {
            setInitialDataSource(formatPortfolioForComparison(detailedPortfolio.symbols, historyDataSource));
        }
    }, [loadingPortfolio, loadingHistory]);


    const formatPortfolioForComparison = (currentSymbols, historySymbols = []) => {
        const historyIndex = historySymbols.reduce((acc, cur) => {
            acc[cur.id] = cur;
            return acc;
        }, {});

        return Object.entries(currentSymbols).map(([id, weight]) => {
            const currentWeight = parseFloat(weight);
            const historyWeight = historyIndex[id]?.weight || 0;
            const delta = currentWeight - historyWeight;
            return {
                id,
                weight: currentWeight,
                delta,
            };
        });
    };

    const handlePortfolioSubmit = (snapshot) => {
        dispatch(saveUserPortfolioSnapshot({ portfolioID: portfolioId, snapshot: snapshot }))
        .then(() => {
            notification.success({
                message: 'Portfolio Updated',
                description: 'The portfolio has been successfully updated.'
            });
        })
        .catch((error) => {
            notification.error({
                message: 'Error Updating Portfolio',
                description: `There was an error updating the portfolio: ${error.toString()}`
            });
        });
    };


    return (
        <>
            <PageHeader
                title={detailedPortfolio?.name}
                actions={[
                    !showSider ? <Button variant={'contained'} key={'show'} onClick={()=>setShowSider(!showSider)}>Show Side Panel</Button> : null
                ]}
            />
            <Layout style={{ height: '100vh', overflow: 'auto' }}>
                {showSider && (
                    <Layout.Sider className={'full right white'} width={400}>
                  <Card
                    title={
                      <ButtonGroup color={'inherit'} size={'small'} variant={'outlined'}>
                          <Button
                            key="back"
                            size={'small'}
                            onClick={() => navigate(-1)}
                            startIcon={<ArrowBackIosNew/>}
                          >
                              Back
                          </Button>
                          <Button size={'small'} onClick={()=>navigate(`/signals/${portfolioId}/history`)}>
                            View History
                          </Button>
                          <Button onClick={()=>setShowSider(!showSider)}>Hide Side</Button>
                      </ButtonGroup>
                    }
                    bordered={false}
                  >
                      <Space direction={'vertical'}>
                          <Typography.Title level={4}>Confirming Signals</Typography.Title>
                          <Typography.Text>
                                Please review the signals below and acknowledge to save the state of the portfolio
                          </Typography.Text>
                          <Button type="primary"
                                    onClick={()=>handlePortfolioSubmit(formatWeightsForSubmission(initialDataSource))}
                                    startIcon={<ThumbUpAltRounded />}
                                    variant={'contained'}
                            >
                                Acknowledge
                            </Button>
                      </Space>
                      <Divider/>
                      <Typography.Title level={4}>
                          Export Options
                      </Typography.Title>
                      <Divider/>
                      <Space>
                          <Button onClick={()=>setShowExport(true)}>Export to CSV</Button>
                          <Button>Export to 3rd Party</Button>
                      </Space>
                      <ThirdPartyExports mode={'straight'} dataSource={initialDataSource}/>
                  </Card>
                </Layout.Sider>
                )}
                <Content className={'px-6'}>
                    <Row>
                        <Col className={'py-4'} span={12}>
                            {initialDataSource.length > 0 ? (
                                <PortfolioSignalsTable
                                    dataSource={initialDataSource.sort((a, b)=>b.weight - a.weight)}
                                    title={<Typography.Title level={3}>Current Portfolio</Typography.Title>}
                                />
                            ) : (
                                <Empty description={'No portfolio data available.'} />
                            )}
                        </Col>
                        <Col className={'py-4'} style={{borderLeft: '1px dashed #ddd'}} span={12}>
                            {historyDataSource.length > 0 ? (
                                <PortfolioSignalsTable
                                    dataSource={historyDataSource}
                                    showDelta={false}
                                    allowExport={false}
                                    title={<Typography.Title level={4} style={{color: 'red'}}>{`Portfolio on ${historyTimestamp}`}</Typography.Title>}
                                    showExport={showExport}
                                    setShowExport={setShowExport}
                                />
                            ) : (
                                <Empty description={'No portfolio history available.'} />
                            )}
                        </Col>
                    </Row>

                </Content>
            </Layout>
        </>
    );
};



export default PortfolioSignals;
